/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectorRef, Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BigFormService, CustomerDetailConfig, FieldConfig, DEFAULT_TENANT_DETAIL_CONFIG, getTenantConfig } from '@flexus/core';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: 'flexus-pinggo-customer-detail',
	templateUrl: './pinggo-customer-detail.component.html',
	styleUrls: ['./pinggo-customer-detail.component.scss']
})
export class PinggoCustomerDetailComponent implements OnInit {
	provinceList: any[] = [];
	@Input() commsOptions: any[] = [];
	private readonly destroyRef = inject(DestroyRef);

	showCustomerDetails = true;
	showContactDetails = true;
	showAddressDetails = true;
	showComplex = true;

	customerFields: FieldConfig[] = [];
	contactFields: FieldConfig[] = [];
	addressCommonFields: FieldConfig[] = [];
	addressHouseFields: FieldConfig[] = [];
	addressFlatFields: FieldConfig[] = [];

	private readonly tenantConfigs: Record<string, CustomerDetailConfig> = {
		default: DEFAULT_TENANT_DETAIL_CONFIG,
		3: {
			customerDetails: {
				visible: true,
				fields: [
					{ controlName: 'first_name', label: 'first_name' },
					{ controlName: 'surname', label: 'surname' },
					// { controlName: 'till_slip', label: 'till_slip' },
					{ controlName: 'id_number', label: 'id_number' },
				]
			},
			contactDetails: {
				visible: true,
				fields: [
					{ controlName: 'contact_number', label: 'contact_number' },
					{ controlName: 'cell_number', label: 'cell_number' },
					{ controlName: 'email', label: 'email' },
					{
						controlName: 'preferred_com_method',
						label: 'preferred_com_method',
						type: 'dropdown',
						optionsSource: 'commsOptions',
						placeholder: 'Select method'
					}
				]
			},
			addressDetails: DEFAULT_TENANT_DETAIL_CONFIG.addressDetails
		},
		4: {
			customerDetails: {
				visible: true,
				fields: [
					{ controlName: 'first_name', label: 'first_name' },
					{ controlName: 'surname', label: 'surname' },
					{ controlName: 'id_number', label: 'id_number' },
				]
			},
			contactDetails: {
				visible: true,
				fields: [
					{ controlName: 'contact_number', label: 'contact_number' },
					
					
				]
			},
			addressDetails: DEFAULT_TENANT_DETAIL_CONFIG.addressDetails
		},
	
		9: {
			customerDetails: DEFAULT_TENANT_DETAIL_CONFIG.customerDetails,
			contactDetails: {
				visible: true,
				fields: [
					{ controlName: 'contact_number', label: 'contact_number' },
					{ controlName: 'email', label: 'email' },
					{
						controlName: 'preferred_com_method',
						label: 'preferred_com_method',
						type: 'dropdown',
						optionsSource: 'commsOptions',
						placeholder: 'Select method'
					}
				]
			},
			addressDetails: {
				visible: true,
				commonFields: [
					{
						controlName: 'province',
						label: 'province',
						type: 'dropdown',
						optionsSource: 'provinceList',
						placeholder: 'Select Province'
					}
				],
				houseEstateFields: [
					{ controlName: 'street_address', label: 'street_address' },
					{ controlName: 'suburb', label: 'suburb' },
					{ controlName: 'city', label: 'city', inputProps: { noEndingSpace: true } }
				],
				flatOfficeFields: [
					{ controlName: 'complex', label: 'Flat/Office Name' },
					{ controlName: 'complex_unit_number', label: 'unit_number' },
					{ controlName: 'street_address', label: 'street_address' }
				]
			}
		},
		11: {
			customerDetails: {
				visible: true,
				fields: [
					{ controlName: 'first_name', label: 'first_name' },
					{ controlName: 'surname', label: 'surname' },
					// { controlName: 'till_slip', label: 'till_slip' },
					// { controlName: 'id_number', label: 'id_number' },
				]
			},
			contactDetails: {
				visible: true,
				fields: [
					{ controlName: 'contact_number', label: 'contact_number' },
					{ controlName: 'cell_number', label: 'cell_number' },
					{ controlName: 'email', label: 'email' },
					// {
					// 	controlName: 'preferred_com_method',
					// 	label: 'preferred_com_method',
					// 	type: 'dropdown',
					// 	optionsSource: 'commsOptions',
					// 	placeholder: 'Select method'
					// }
				]
			},
			addressDetails: DEFAULT_TENANT_DETAIL_CONFIG.addressDetails
		},
		// Example override:
		// 101: {
		// 	customerDetails: { visible: true, fields: [{ controlName: 'first_name', label: 'first_name' }] },
		// 	contactDetails: { visible: false, fields: [] },
		// 	addressDetails: { visible: true, commonFields: [], houseEstateFields: [], flatOfficeFields: [] }
		// }
	};

	constructor(public _store: Store<any>, public bf: BigFormService, private cd: ChangeDetectorRef) {}

	ngOnInit(): void {
		const provinceList = this.bf?.storeObject?.selectedContext?.provinceList;
		this.provinceList = provinceList.map((province) => ({ display: province?.name, value: province?.name, id: province?.id }));

		this.applyTenantConfig(this.bf.bigForm.get('selected_company')?.value ?? null);

		// Ensure insured_address FormGroup has all required controls
		const insuredAddress = this.bf.bigForm.get('insured_address');
		if (insuredAddress instanceof UntypedFormGroup) {
			if (!insuredAddress.get('province')) {
				insuredAddress.addControl('province', new UntypedFormControl(''));
			}
			if (!insuredAddress.get('postal_code')) {
				insuredAddress.addControl('postal_code', new UntypedFormControl(''));
			}
			if (!insuredAddress.get('complex_block')) {
				insuredAddress.addControl('complex_block', new UntypedFormControl(''));
			}
		}

		// Subscribe to tenant changes
		this.bf.bigForm
			.get('selected_company')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((tenantId) => {
				this.applyTenantConfig(tenantId ?? null);
			});

		this.bf.bigForm
			.get('tenant_document_config')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.applyTenantConfig(this.bf.bigForm.get('selected_company')?.value ?? null);
			});

		// Initialize controls with extracted document data if available
		// Data is guaranteed to be present due to serverCall completing before navigation
		this.initializeFromExtractedData();
	}

	private initializeFromExtractedData(): void {
		const extractedData = this.bf.bigForm.get('extracted_document_data')?.value;
		if (!extractedData) {
			return;
		}

		// Map customer data to client_details
		const clientDetails = this.bf.bigForm.get('client_details');
		if (clientDetails && extractedData.customer) {
			const customer = extractedData.customer;
			this.patchIfEmpty(clientDetails, 'first_name', customer.first_name);
			this.patchIfEmpty(clientDetails, 'surname', customer.surname);
		}

		// Map sales order data to client_details
		if (clientDetails && extractedData.sales_order) {
			const salesOrder = extractedData.sales_order;
			this.patchIfEmpty(clientDetails, 'sales_order_number', salesOrder.order_number);
			this.patchIfEmpty(clientDetails, 'customer_reference', salesOrder.customer_number);
		}

		// Map customer contact data to contact_details
		const contactDetails = this.bf.bigForm.get('contact_details');
		if (contactDetails && extractedData.customer) {
			const customer = extractedData.customer;
			this.patchIfEmpty(contactDetails, 'cell_number', customer.mobile_1);
			this.patchIfEmpty(contactDetails, 'contact_number', customer.mobile_2 || customer.home_landline);
			this.patchIfEmpty(contactDetails, 'email', customer.email);
		}

		// Map installation_address to insured_address
		const insuredAddress = this.bf.bigForm.get('insured_address');
		if (insuredAddress && extractedData.installation_address) {
			const address = extractedData.installation_address;
			this.patchIfEmpty(insuredAddress, 'street_address', address.street);
			this.patchIfEmpty(insuredAddress, 'suburb', address.suburb);
			this.patchIfEmpty(insuredAddress, 'city', address.city);
			this.patchIfEmpty(insuredAddress, 'postal_code', address.postal_code);
			// Match province to dropdown value
			if (address.province) {
				const matchedProvince = this.findMatchingProvince(address.province);
				if (matchedProvince) {
					this.patchIfEmpty(insuredAddress, 'province', matchedProvince);
				}
			}
		}

		// Map ship_to_address from sales_order as fallback for insured_address
		if (insuredAddress && extractedData.sales_order?.ship_to_address) {
			const shipAddress = extractedData.sales_order.ship_to_address;
			this.patchIfEmpty(insuredAddress, 'street_address', shipAddress.street);
			this.patchIfEmpty(insuredAddress, 'suburb', shipAddress.suburb);
			this.patchIfEmpty(insuredAddress, 'postal_code', shipAddress.postal_code);
		}
	}

	private patchIfEmpty(formGroup: any, controlName: string, value: any): void {
		if (!value) return;
		const control = formGroup.get(controlName);
		if (control && !control.value) {
			control.patchValue(value);
		}
	}

	private findMatchingProvince(extractedProvince: string): string | null {
		if (!extractedProvince || !this.provinceList?.length) return null;
		
		const normalized = extractedProvince.toLowerCase().trim();
		
		// Try exact match first
		const exactMatch = this.provinceList.find(
			p => p.value?.toLowerCase() === normalized
		);
		if (exactMatch) return exactMatch.value;
		
		// Try partial match (extracted province contains or is contained by list value)
		const partialMatch = this.provinceList.find(
			p => p.value?.toLowerCase().includes(normalized) || normalized.includes(p.value?.toLowerCase())
		);
		if (partialMatch) return partialMatch.value;
		
		// Common abbreviation mappings for South African provinces
		const abbrevMap: Record<string, string> = {
			'gp': 'gauteng',
			'wc': 'western cape',
			'kzn': 'kwazulu-natal',
			'ec': 'eastern cape',
			'fs': 'free state',
			'lp': 'limpopo',
			'mp': 'mpumalanga',
			'nw': 'north west',
			'nc': 'northern cape'
		};
		
		const expanded = abbrevMap[normalized];
		if (expanded) {
			const abbrevMatch = this.provinceList.find(
				p => p.value?.toLowerCase().includes(expanded)
			);
			if (abbrevMatch) return abbrevMatch.value;
		}

		return null;
	}

	setArea(event: string): void {
		const area = this.provinceList.find((province) => province?.value === event)?.id;
		const areaControl = this.bf.bigForm.get('area_code');
		if (areaControl) {
			areaControl.patchValue(area);
		}
	}

	// ngAfterViewInit(): void {
	// 	// Component is fully initialized
	// }

	getControl(groupName: string, controlName: string): UntypedFormControl | null {
		const group = this.bf.bigForm.get(groupName);
		if (group instanceof UntypedFormGroup) {
			return group.get(controlName) as UntypedFormControl;
		}
		return null;
	}

	resolveOptions(field: FieldConfig): any[] {
		if (field.optionsSource === 'commsOptions') {
			return this.commsOptions ?? [];
		}
		if (field.optionsSource === 'provinceList') {
			return this.provinceList ?? [];
		}
		return field.options ?? [];
	}

	handleDropdownSelect(field: FieldConfig, event: string): void {
		if (field.controlName === 'province') {
			this.setArea(event);
		}
	}

	private applyTenantConfig(tenantId: number | null): void {
		const metadata = this.bf.bigForm.get('tenant_document_config')?.value?.metadata;
		const config = getTenantConfig(tenantId, metadata, this.tenantConfigs);

		this.showCustomerDetails = !!config.customerDetails?.visible;
		this.showContactDetails = !!config.contactDetails?.visible;
		this.showAddressDetails = !!config.addressDetails?.visible;

		this.customerFields = config.customerDetails?.fields ?? [];
		this.contactFields = config.contactDetails?.fields ?? [];
		this.addressCommonFields = this.buildUnifiedAddressFields(config);
		this.addressHouseFields = [];
		this.addressFlatFields = [];

		this.ensureFormControls();
	}

	private buildUnifiedAddressFields(config: CustomerDetailConfig): FieldConfig[] {
		const allAddressFields = [
			...(config.addressDetails?.commonFields ?? []),
			...(config.addressDetails?.houseEstateFields ?? []),
			...(config.addressDetails?.flatOfficeFields ?? [])
		];
		const byControlName = new Map<string, FieldConfig>();
		allAddressFields.forEach((field) => {
			if (field?.controlName && !byControlName.has(field.controlName)) {
				byControlName.set(field.controlName, field);
			}
		});

		const allowedAndOrderedControls = ['complex', 'street_address', 'suburb', 'city', 'postal_code', 'province'];
		return allowedAndOrderedControls
			.map((controlName) => byControlName.get(controlName))
			.filter((field): field is FieldConfig => !!field)
			.map((field) => {
				if (field.controlName === 'complex') {
					return { ...field, label: 'Estate / Office Park' };
				}
				return field;
			});
	}

	private ensureFormControls(): void {
		this.ensureSectionControls('client_details', this.customerFields);
		this.ensureSectionControls('contact_details', this.contactFields);
		this.ensureSectionControls('insured_address', this.addressCommonFields);
	}

	private ensureSectionControls(groupName: string, fields: FieldConfig[]): void {
		const group = this.bf.bigForm.get(groupName);
		if (!(group instanceof UntypedFormGroup)) {
			return;
		}
		fields.forEach((field) => {
			if (!field?.controlName) return;
			if (!group.get(field.controlName)) {
				group.addControl(field.controlName, new UntypedFormControl('', field.validators ?? []));
			}
		});
	}
}


